import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import HospitalsPage from "@/pages/HospitalsPage";
import StaffPage from "@/pages/StaffPage";
import PatientsPage from "@/pages/PatientsPage";
import PatientDetailPage from "@/pages/PatientDetailPage";
import OPDPage from "@/pages/OPDPage";
import IPDPage from "@/pages/IPDPage";
import AdmissionDetailPage from "@/pages/AdmissionDetailPage";
import ReportsPage from "@/pages/ReportsPage";
import CustomFieldsPage from "@/pages/CustomFieldsPage";
import MastersPage from "@/pages/MastersPage";
import SettingsPage from "@/pages/SettingsPage";
import BrandingPage from "@/pages/BrandingPage";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function ReportsGate({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  // If admin disabled reports for users, redirect users away from /reports.
  if (user.role === "user" && user.hospital?.reports_enabled_for_users === false) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function OPDGate({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  // If the hospital has OPD module disabled, redirect away from /opd.
  if (user.hospital?.opd_module_enabled === false) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--hms-app)" }}>
      <div className="font-heading text-sm uppercase tracking-widest text-[var(--hms-text-2)]">
        Loading workspace…
      </div>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<Protected><DashboardLayout /></Protected>}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/patients" element={<PatientsPage />} />
              <Route path="/patients/:mrn" element={<PatientDetailPage />} />
              <Route path="/opd" element={<OPDGate><OPDPage /></OPDGate>} />
              <Route path="/ipd" element={<IPDPage />} />
              <Route path="/admissions/:aid" element={<AdmissionDetailPage />} />
              <Route path="/reports" element={<ReportsGate><ReportsPage /></ReportsGate>} />
              <Route path="/custom-fields" element={
                <Protected roles={["admin", "superuser"]}><CustomFieldsPage /></Protected>
              } />
              <Route path="/masters" element={
                <Protected><MastersPage /></Protected>
              } />
              <Route path="/staff" element={
                <Protected roles={["admin", "superuser"]}><StaffPage /></Protected>
              } />
              <Route path="/hospitals" element={
                <Protected roles={["superuser"]}><HospitalsPage /></Protected>
              } />
              <Route path="/branding" element={
                <Protected roles={["superuser"]}><BrandingPage /></Protected>
              } />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}
